import { type CSSProperties, type FC, type RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import type { Virtualizer } from '@tanstack/react-virtual';
import { _isNil } from '@/littledash';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import { errorToast } from '@/helpers';
import InVivoError from '@/model/InVivoError';
import type {
  DataTableCellIdCoordinate,
  DataTableCellIndexCoordinate,
  DataTableDirection,
  DataTableEditingCellChangeEvent,
} from '@/components/UI/DataTableV2/DataTable.model';
import { DataTableEvent } from '@/components/UI/DataTableV2/DataTable.model';
import { useDataTable } from '@/components/UI/DataTableV2/DataTableProvider';
import { computeEditorPosition, type EditorRect, hasCellDrifted } from './editorPosition';
import { resolveSubEditorKind, type SubEditorKind } from './subEditorKind';
import { TextCellEditor } from './editors/TextCellEditor';
import { DateCellEditor } from './editors/DateCellEditor';
import { SelectCellEditor } from './editors/SelectCellEditor';
import styles from './SingleCellEditor.module.scss';

/**
 * Props every concrete sub-editor (TextCellEditor / DateCellEditor /
 * SelectCellEditor, added in Tasks 4-6) receives from the shell.
 *
 * The shell owns activation, positioning and teardown; a sub-editor is only
 * responsible for rendering its input and reading/writing cell values through
 * the service. It must stay DOM-independent of the underlying grid cell: the
 * stable `idCoordinate` is captured at activation, so a sub-editor reads the
 * current value via `dataTableService.cellData(idCoordinate)` and commits via
 * `dataTableService.updateCells(...)` — never by touching the cell DOM.
 */
export interface SubEditorProps {
  /** Stable API-id coordinate of the cell being edited, captured at activation. */
  idCoordinate: DataTableCellIdCoordinate;
  /** Index coordinate of the cell being edited. */
  indexCoordinate: DataTableCellIndexCoordinate;
  /** Seed content supplied at activation (e.g. a typed character), if any. */
  content?: string;
  /**
   * Direction the selection entered the cell from, if activation came from
   * keyboard navigation. Sub-editors use it to decide cursor placement.
   */
  direction?: DataTableDirection;
  /**
   * Commit the edited value and stop editing. Passing `undefined`/empty is a
   * clear. A sub-editor may pass a `moveSelection` direction so the shell moves
   * the selection after committing (Tasks 4-6 wire this to the service).
   * Date editors pass `validate: true` to trigger validation after updateCells.
   */
  onCommit: (value: string | null, options?: { moveSelection?: DataTableDirection; validate?: boolean }) => void;
  /** Discard any pending change and stop editing without writing. */
  onCancel: () => void;
}

interface ActiveEditorState {
  indexCoordinate: DataTableCellIndexCoordinate;
  idCoordinate: DataTableCellIdCoordinate;
  content?: string;
  direction?: DataTableDirection;
  kind: SubEditorKind;
  rect: EditorRect;
}

/**
 * Rendered height of the drift chip in px. Fixed (not derived from cell height)
 * so the chip's real box — font-size-8 text plus vertical padding — fits within
 * the space reserved above the editor and never overlaps its top edge. Matches
 * the visual weight of the RemoteSelectionsOverlay name chip.
 */
const CHIP_HEIGHT = 18;

interface SingleCellEditorProps {
  scrollElementRef: RefObject<HTMLDivElement | null>;
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
  columnVirtualizer: Virtualizer<HTMLDivElement, Element>;
  pinnedColumnCount: number;
  rowHeaderWidth: number;
  stickyWidth: number;
  totalHeaderHeight: number;
  testId?: string;
}

/**
 * Viewport-fixed floating editor shell for the active cell.
 *
 * Subscribes to `EditingCellChange`: when a cell becomes active it scrolls the
 * cell into view (the shared `useAutoScroll` deliberately skips scrolling on
 * activation), computes a screen position once on the next animation frame, and
 * pins itself there. The position does NOT track subsequent scrolling — instead
 * a lightweight scroll watcher toggles a row/column chip once the origin cell
 * has drifted out from under the editor.
 *
 * Renders the bordered editing box and mounts the matching text/date/select
 * sub-editor for the active cell via {@link SubEditorProps}.
 */
export const SingleCellEditor: FC<SingleCellEditorProps> = ({
  scrollElementRef,
  rowVirtualizer,
  columnVirtualizer,
  pinnedColumnCount,
  rowHeaderWidth,
  stickyWidth,
  totalHeaderHeight,
  testId = 'single-cell-editor',
}) => {
  const { dataTableService } = useDataTable();
  const [active, setActive] = useState<ActiveEditorState | undefined>(undefined);
  const [drifted, setDrifted] = useState(false);

  useEffect(() => {
    const abortController = new AbortController();
    let positionFrame = 0;

    const positionFor = (indexCoordinate: DataTableCellIndexCoordinate): EditorRect =>
      computeEditorPosition({
        column: indexCoordinate.column,
        row: indexCoordinate.row,
        pinnedColumnCount,
        dimensions: dataTableService.dimensions,
        rowHeaderWidth,
        stickyWidth,
        totalHeaderHeight,
        scrollLeft: scrollElementRef.current?.scrollLeft ?? 0,
        scrollTop: scrollElementRef.current?.scrollTop ?? 0,
      });

    const handleEditingCellChange = (event: DataTableEditingCellChangeEvent): void => {
      const editingCell = event.detail.editingCell;

      if (positionFrame !== 0) {
        cancelAnimationFrame(positionFrame);
        positionFrame = 0;
      }

      if (_isNil(editingCell)) {
        setActive(undefined);
        setDrifted(false);
        return;
      }

      const indexCoordinate = editingCell.coordinate;
      const idCoordinate = dataTableService.fromIndexCoordinate(indexCoordinate);
      if (_isNil(idCoordinate)) {
        return;
      }

      const scrollableColumnIndex = indexCoordinate.column - pinnedColumnCount;
      rowVirtualizer.scrollToIndex(indexCoordinate.row, { align: 'auto' });
      if (scrollableColumnIndex >= 0) {
        columnVirtualizer.scrollToIndex(scrollableColumnIndex, { align: 'auto' });
      }

      positionFrame = requestAnimationFrame(() => {
        setActive({
          indexCoordinate,
          idCoordinate,
          content: editingCell.content,
          direction: editingCell.direction,
          kind: resolveSubEditorKind(dataTableService.columnByIndex(indexCoordinate.column), dataTableService.readonly),
          rect: positionFor(indexCoordinate),
        });
        setDrifted(false);
      });
    };

    dataTableService.subscribe(DataTableEvent.EditingCellChange, handleEditingCellChange, abortController.signal);
    return () => {
      if (positionFrame !== 0) {
        cancelAnimationFrame(positionFrame);
      }
      abortController.abort('teardown');
    };
  }, [
    dataTableService,
    rowVirtualizer,
    columnVirtualizer,
    pinnedColumnCount,
    rowHeaderWidth,
    stickyWidth,
    totalHeaderHeight,
    scrollElementRef,
  ]);

  useEffect(() => {
    const scrollElement = scrollElementRef.current;
    if (_isNil(active) || _isNil(scrollElement)) {
      return;
    }

    const evaluateDrift = (): void => {
      const current = computeEditorPosition({
        column: active.indexCoordinate.column,
        row: active.indexCoordinate.row,
        pinnedColumnCount,
        dimensions: dataTableService.dimensions,
        rowHeaderWidth,
        stickyWidth,
        totalHeaderHeight,
        scrollLeft: scrollElement.scrollLeft,
        scrollTop: scrollElement.scrollTop,
      });
      setDrifted(hasCellDrifted(active.rect, current));
    };

    scrollElement.addEventListener('scroll', evaluateDrift, { passive: true });
    return () => {
      scrollElement.removeEventListener('scroll', evaluateDrift);
    };
  }, [active, dataTableService, pinnedColumnCount, rowHeaderWidth, stickyWidth, totalHeaderHeight, scrollElementRef]);

  const editorStyle = useMemo<CSSProperties | undefined>(() => {
    if (_isNil(active)) {
      return undefined;
    }
    return {
      left: `${active.rect.left}px`,
      top: `${active.rect.top}px`,
      width: `${active.rect.width}px`,
      height: `${active.rect.height}px`,
    };
  }, [active]);

  const handleCommit = useCallback(
    (value: string | null, options?: { moveSelection?: DataTableDirection; validate?: boolean }): void => {
      if (_isNil(active)) {
        return;
      }

      dataTableService
        .updateCells([
          {
            column_id: active.idCoordinate.column,
            row_id: active.idCoordinate.row,
            value,
          },
        ])
        .then(() => {
          if (options?.validate) {
            dataTableService.validate();
          }
        })
        .catch((err) => {
          ExceptionHandler.captureException(
            new InVivoError('Failed to commit cell edit', { cause: err, slug: 'datatable-cell-edit-commit-error' })
          );
          errorToast('Failed to save cell. Please try again.');
        });

      dataTableService.stopEditing();

      if (_isNil(options?.moveSelection)) {
        return;
      }

      const workflowStepExecuted = dataTableService.workflowService.workflowActionExit(active.idCoordinate.column);
      if (!workflowStepExecuted) {
        dataTableService.moveSelection(options.moveSelection);
      }
    },
    [active, dataTableService]
  );

  const handleCancel = useCallback((): void => {
    dataTableService.stopEditing();
  }, [dataTableService]);

  if (_isNil(active) || _isNil(editorStyle)) {
    return null;
  }

  const chipLabel = `${dataTableService.rowByIndex(active.indexCoordinate.row)?.animal?.name ?? ''} · ${
    dataTableService.columnByIndex(active.indexCoordinate.column)?.name ?? ''
  }`;

  return (
    <>
      {drifted && (
        <div
          className={styles.chip}
          data-testid={`${testId}-chip`}
          style={{
            left: `${active.rect.left}px`,
            top: `${active.rect.top - CHIP_HEIGHT}px`,
            height: `${CHIP_HEIGHT}px`,
          }}
        >
          <span className={styles.chipLabel}>{chipLabel}</span>
        </div>
      )}
      <div className={styles.editor} data-testid={testId} data-sub-editor={active.kind} style={editorStyle}>
        {active.kind === 'text' && (
          <TextCellEditor
            idCoordinate={active.idCoordinate}
            indexCoordinate={active.indexCoordinate}
            content={active.content}
            direction={active.direction}
            onCommit={handleCommit}
            onCancel={handleCancel}
          />
        )}
        {active.kind === 'date' && (
          <DateCellEditor
            idCoordinate={active.idCoordinate}
            indexCoordinate={active.indexCoordinate}
            content={active.content}
            direction={active.direction}
            onCommit={handleCommit}
            onCancel={handleCancel}
          />
        )}
        {active.kind === 'select' && (
          <SelectCellEditor
            idCoordinate={active.idCoordinate}
            indexCoordinate={active.indexCoordinate}
            content={active.content}
            direction={active.direction}
            onCommit={handleCommit}
            onCancel={handleCancel}
          />
        )}
      </div>
    </>
  );
};
